/**
 * Modal de subida de fotos a la galería — Fase 10d.
 *
 * Acepta múltiples archivos (drag&drop o file picker), una caption
 * opcional aplicada a TODAS las fotos del request, y dispara la subida
 * vía callback del caller (mantenemos la I/O fuera del modal).
 */

import './upload-modal.css';

const MAX_FILES = 5;
const MAX_BYTES = 10 * 1024 * 1024;
const ALLOWED_MIMES = new Set<string>([
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'image/avif',
  'image/bmp',
]);
const MAX_CAPTION = 200;

export interface UploadModalResult {
  files: File[];
  caption: string | null;
}

interface ModalRefs {
  dialog: HTMLDialogElement;
  fileInput: HTMLInputElement;
  fileList: HTMLElement;
  fileSummary: HTMLElement;
  captionTa: HTMLTextAreaElement;
  captionCount: HTMLElement;
  errorBox: HTMLElement;
  uploadBtn: HTMLButtonElement;
  cancelBtn: HTMLButtonElement;
  pickBtn: HTMLButtonElement;
}

let refs: ModalRefs | null = null;

export function openUploadModal(): Promise<UploadModalResult | null> {
  const r = ensureModal();
  resetForm(r);

  return new Promise<UploadModalResult | null>((resolve) => {
    let resolved = false;
    const finish = (value: UploadModalResult | null): void => {
      if (resolved) return;
      resolved = true;
      r.dialog.close();
      r.uploadBtn.removeEventListener('click', onUpload);
      r.cancelBtn.removeEventListener('click', onCancel);
      r.dialog.removeEventListener('cancel', onCancel);
      r.dialog.removeEventListener('click', onBackdrop);
      r.fileInput.removeEventListener('change', onFilesChange);
      r.captionTa.removeEventListener('input', onCaptionInput);
      r.pickBtn.removeEventListener('click', onPick);
      resolve(value);
    };
    const onCancel = (ev?: Event): void => {
      ev?.preventDefault();
      finish(null);
    };
    const onBackdrop = (ev: MouseEvent): void => {
      if (ev.target === r.dialog) finish(null);
    };
    const onPick = (): void => r.fileInput.click();
    const onFilesChange = (): void => updateFileList(r);
    const onCaptionInput = (): void => updateCount(r);
    const onUpload = (): void => {
      const files = Array.from(r.fileInput.files ?? []);
      if (files.length === 0) {
        showError(r, 'Selecciona al menos una foto.');
        return;
      }
      if (files.length > MAX_FILES) {
        showError(r, `Máximo ${MAX_FILES} fotos por subida.`);
        return;
      }
      for (const f of files) {
        if (!ALLOWED_MIMES.has(f.type.toLowerCase())) {
          showError(
            r,
            `Tipo no soportado en "${f.name}": ${f.type || 'desconocido'}.`,
          );
          return;
        }
        if (f.size > MAX_BYTES) {
          showError(
            r,
            `"${f.name}" supera el límite de ${MAX_BYTES / 1024 / 1024} MB.`,
          );
          return;
        }
      }
      const captionTrimmed = r.captionTa.value.trim();
      if (captionTrimmed.length > MAX_CAPTION) {
        showError(r, `El pie de foto no puede superar ${MAX_CAPTION} caracteres.`);
        return;
      }
      finish({
        files,
        caption: captionTrimmed.length === 0 ? null : captionTrimmed,
      });
    };

    r.uploadBtn.addEventListener('click', onUpload);
    r.cancelBtn.addEventListener('click', onCancel);
    r.dialog.addEventListener('cancel', onCancel);
    r.dialog.addEventListener('click', onBackdrop);
    r.fileInput.addEventListener('change', onFilesChange);
    r.captionTa.addEventListener('input', onCaptionInput);
    r.pickBtn.addEventListener('click', onPick);

    r.dialog.showModal();
    r.pickBtn.focus();
  });
}

// ─── Helpers ──────────────────────────────────────────────────────────

function ensureModal(): ModalRefs {
  if (refs !== null) return refs;
  const dialog = document.createElement('dialog');
  dialog.className = 'gu-dialog';
  dialog.innerHTML = `
    <form class="gu-card" method="dialog" novalidate>
      <h2 class="gu-title">SUBIR FOTOS</h2>

      <div class="gu-pick">
        <button type="button" class="gu-pick-btn">Seleccionar archivos</button>
        <span class="gu-summary">Ningún archivo seleccionado.</span>
        <input
          type="file"
          class="gu-input"
          accept="image/jpeg,image/png,image/webp,image/gif,image/avif,image/bmp"
          multiple
          hidden
        />
      </div>
      <ul class="gu-files"></ul>

      <label class="gu-field">
        <span class="gu-label">Pie de foto (opcional, mismo para todas)</span>
        <textarea
          class="gu-caption"
          rows="2"
          maxlength="200"
          placeholder="Algo corto que las describa…"
        ></textarea>
        <small class="gu-count" aria-live="polite">0 / ${MAX_CAPTION}</small>
      </label>

      <p class="gu-error" role="alert" hidden></p>

      <div class="gu-actions">
        <button type="button" class="gu-btn gu-cancel">Cancelar</button>
        <button type="button" class="gu-btn gu-upload">Subir</button>
      </div>
    </form>
  `;
  document.body.appendChild(dialog);

  const get = <T extends HTMLElement>(sel: string): T => {
    const el = dialog.querySelector<T>(sel);
    if (!el) throw new Error(`[upload-modal] missing ${sel}`);
    return el;
  };
  refs = {
    dialog,
    fileInput: get<HTMLInputElement>('.gu-input'),
    fileList: get<HTMLElement>('.gu-files'),
    fileSummary: get<HTMLElement>('.gu-summary'),
    captionTa: get<HTMLTextAreaElement>('.gu-caption'),
    captionCount: get<HTMLElement>('.gu-count'),
    errorBox: get<HTMLElement>('.gu-error'),
    uploadBtn: get<HTMLButtonElement>('.gu-upload'),
    cancelBtn: get<HTMLButtonElement>('.gu-cancel'),
    pickBtn: get<HTMLButtonElement>('.gu-pick-btn'),
  };
  return refs;
}

function resetForm(r: ModalRefs): void {
  r.fileInput.value = '';
  r.captionTa.value = '';
  r.errorBox.hidden = true;
  r.errorBox.textContent = '';
  updateFileList(r);
  updateCount(r);
}

function updateFileList(r: ModalRefs): void {
  const files = Array.from(r.fileInput.files ?? []);
  r.fileList.innerHTML = files
    .map(
      (f) =>
        `<li class="gu-file">${escapeHtml(f.name)} <small>(${formatBytes(f.size)})</small></li>`,
    )
    .join('');
  r.fileSummary.textContent =
    files.length === 0
      ? 'Ningún archivo seleccionado.'
      : files.length === 1
        ? '1 archivo'
        : `${files.length} archivos`;
}

function updateCount(r: ModalRefs): void {
  r.captionCount.textContent = `${r.captionTa.value.length} / ${MAX_CAPTION}`;
}

function showError(r: ModalRefs, msg: string): void {
  r.errorBox.textContent = msg;
  r.errorBox.hidden = false;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
